"use client";

import Image from "next/image";
import { GraphsIcon } from "@/components/ui/icons/grpahs";
import { ChartIcon } from "@/components/ui/icons/chart-icon";
import { BellIcon } from "@/components/ui/icons/bell-icon";
import { DiamondIcon } from "@/components/ui/icons/diamond";
import { useModal } from "../providers/modal-provider";
import { modalIds } from "./constants";
import { Drawer, DrawerContent } from "@workspace/ui/components/drawer";
import { Dialog, DialogContent } from "@workspace/ui/components/dialog";
import { CrossIcon } from "@/components/ui/icons/cross-icon";
import { Button } from "@workspace/ui/components/button";
import { useCallback, useEffect, useRef } from "react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useQuizState } from "@/components/quiz/hooks/use-quiz-state";
import { sendEvent, EventNames } from "@/lib/utils/analytics";
import { type TouchpointType } from "@/lib/quiz-attribution";
import { FIRST_QUESTION_ID, questions } from "@/components/quiz/utils/utils";
import { QUIZ_SOURCE } from "@/components/quiz/constants";

const CDN_BASE = "https://cdn.inito.com/inito_website/home";
const startQuizMobileImage = `${CDN_BASE}/start-quiz-mobile.png`;
const startQuizDesktopImage = `${CDN_BASE}/start-quiz-desktop.png`;

const QUIZ_INTRO_POINTS = [
  {
    icon: <ChartIcon bold className="h-[26px] w-[26px]" />,
    text: "Take our hormone quiz",
  },
  {
    icon: <BellIcon />,
    text: "Get a personalised sample Inito hormone chart",
  },
  {
    icon: <DiamondIcon />,
    text: "See how Inito gives detailed fertility insights tailored to your body",
  },
];

export function StartQuizModal() {
  const { modalId, handleCloseModal, handleOpenModal, modalContent } = useModal();
  const isOpen = modalId === modalIds.quiz;
  const isDesktop = useMediaQuery("(min-width: 640px)");
  const { isResuming, quizState, startQuiz } = useQuizState();
  const displayedRef = useRef(false);

  const touchpointType = modalContent?.touchpointType;
  const sheetType = isResuming ? "resume" : "intro";

  useEffect(() => {
    if (isOpen && !displayedRef.current && quizState.status !== 2) {
      displayedRef.current = true;
      sendEvent(EventNames.BOTTOM_SHEET_DISPLAYED, {
        quiz_source: QUIZ_SOURCE,
        sheet_type: sheetType,
        touchpoint_type: touchpointType,
      });
    }
    if (!isOpen) {
      displayedRef.current = false;
    }
  }, [isOpen, sheetType, touchpointType, quizState.status]);

  useEffect(() => {
    if (isOpen && quizState.status === 2) {
      handleOpenModal(modalIds.quizQuestionnaire, { touchpointType });
    }
  }, [isOpen, quizState.status, handleOpenModal, touchpointType]);

  const handleStartQuiz = useCallback(() => {
    const clickText = isResuming ? "resume_quiz" : "start_quiz";
    const quizScreenName = isResuming
      ? (questions[quizState.lastQuestion]?.id ?? FIRST_QUESTION_ID)
      : FIRST_QUESTION_ID;

    sendEvent(EventNames.BOTTOM_SHEET_CLICKED, {
      quiz_source: QUIZ_SOURCE,
      sheet_type: sheetType,
      click_text: clickText,
      touchpoint_type: touchpointType,
      quiz_screen_name: quizScreenName,
    });

    if (quizState.status === 0) {
      startQuiz();
    }
    handleOpenModal(modalIds.quizQuestionnaire, { touchpointType });
  }, [handleOpenModal, quizState.status, startQuiz, isResuming, sheetType, touchpointType]);

  const handleClose = useCallback(() => {
    sendEvent(EventNames.BOTTOM_SHEET_CLICKED, {
      quiz_source: QUIZ_SOURCE,
      sheet_type: sheetType,
      click_text: "exit_quiz",
      touchpoint_type: touchpointType,
    });
    handleCloseModal();
  }, [handleCloseModal, sheetType, touchpointType]);

  const content = (
    <div className="flex flex-col bg-transparent h-full rounded-t-2xl overflow-hidden">
      {/* Hero image */}
      <div className="relative w-full aspect-[5/3] shrink-0">
        <Image
          src={isDesktop ? startQuizDesktopImage : startQuizMobileImage}
          alt=""
          fill
          className="object-cover"
        />

        {/* Translucent close button */}
        <Button
          variant="ghost"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex size-9 shrink-0 items-center justify-center rounded-full bg-black/[0.22] p-0 text-white backdrop-blur-[6px] transition-colors hover:bg-black/30"
        >
          <CrossIcon className="size-4 md:size-6" color="#FFFFFF" />
        </Button>

        {/* Logo badge — straddles the image / content boundary */}
        <div className="absolute left-7 -bottom-8 flex items-center justify-center rounded-full bg-[#F3F4ED] ring-[6px] ring-white sm:left-8">
          <GraphsIcon className="size-16" />
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 min-h-0 flex-col px-5 pt-12 sm:px-6 sm:pt-10">
        <div className="flex-1 min-h-0 overflow-y-auto pr-[2.205rem] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex flex-col gap-8">
            {/* Title */}
            <h2 className="title-24 lg:title-32 text-[#232221]">
              {isResuming ? "Resume quiz" : "Thinking how Inito will help in your fertility journey?"}
            </h2>

            {/* Bullet points */}
            <ul className="flex flex-col gap-4 sm:gap-3.5">
              {QUIZ_INTRO_POINTS.map(({ icon, text }) => (
                <li key={text} className="flex items-center gap-[1.378rem]">
                  <span className="shrink-0">{icon}</span>
                  <span className="body-18 text-[#232221] sm:title-14 sm:font-normal">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA — always visible at bottom */}
        <div className="shrink-0 pb-5 pt-4 sm:pb-6">
          <Button
            variant="default"
            onClick={handleStartQuiz}
            className="w-full md:title-18"
          >
            {isResuming ? "Resume Quiz" : "Start Quiz"}
          </Button>
        </div>
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent
          showCloseButton={false}
          className="!z-[52] p-0 overflow-hidden border-none bg-white rounded-2xl w-full !max-w-[500px] as"
          overlayClassName="!bg-transparent !backdrop-blur-none"
        >
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent
        className="!z-[52] p-0 border-none bg-white !rounded-t-2xl !max-h-[95dvh]"
        overlayClassName="!bg-transparent !backdrop-blur-none"
      >
        {content}
      </DrawerContent>
    </Drawer>
  );
}
